import { useMemo } from "react"
import { toast } from "react-toastify"
import { showErrorToast } from "~/utils/toaster"

import { api } from "~/utils/api"

export const useExtraDataValues = (columnName: string) =>
  api.sku.getExtraDataValues.useQuery({ columnName })

export const useCollectionSkuExtraDataValues = (columnName: string) =>
  api.sku.getCollectionSkuExtraDataValues.useQuery({ columnName })

export const useExtraDataFormattedKeys = () => {
  const queryInfo = api.sku.getExtraDataKeys.useQuery()

  const memoizedData = useMemo(() => {
    return queryInfo.data?.map((key) => key.key + "(extradata)")
  }, [queryInfo.data])

  return { ...queryInfo, data: memoizedData }
}

export const useCollectionSkuExtraDataKeys = () => {
  const queryInfo = api.sku.getCollectionSkuExtraDataKeys.useQuery()

  const memoizedData = useMemo(() => {
    return queryInfo.data?.map((key) => key.key + "(collection-extradata)")
  }, [queryInfo.data])

  return { ...queryInfo, data: memoizedData }
}

export const useIncomingOrderExtraDataKeys = () => {
  const queryInfo = api.sku.getIncomingOrderExtraDataKeys.useQuery()

  const memoizedData = useMemo(() => {
    return queryInfo.data?.map((key) => key.key + "(incoming-order-extradata)")
  }, [queryInfo.data])

  return { ...queryInfo, data: memoizedData }
}

export const useExtraDataTypeMap = () => {
  return api.sku.getExtraDataTypes.useQuery()
}

export const useExtraDataAggregationMap = () => {
  return api.sku.getExtraDataAggregation.useQuery()
}

export const useExtraDataDisplayNameMap = (enabled: boolean = true) => {
  return api.sku.getExtraDataDisplayNameMap.useQuery(undefined, { enabled })
}

export const useExtraDataMetadata = (input: { tableNames: string[] }) => {
  return api.sku.getExtraFieldMetadata.useQuery(input)
}

export const useUpdateSkuExtraDataMetadata = () => {
  const utils = api.useUtils()
  return api.sku.updateSkuExtraFieldMetadata.useMutation({
    onSuccess: () => {
      utils.sku.getExtraFieldMetadata.invalidate()
      utils.sku.getExtraDataDisplayNameMap.invalidate()
      toast.success(
        "Column configuration updated. Please refresh the page in 5 minutes once data has been updated in our systems",
      )
    },
  })
}

export const useMakeVariantsNoos = () => {
  return api.sku.changeVariantsToNOOS.useMutation({
    onSuccess: async () => {
      toast.success("Variants updated to NOOS")
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

export const useGetNoosVariants = (variantIds: string[]) => {
  const queryInfo = api.sku.getNOOSVariants.useQuery(
    { variantIds },
    { enabled: variantIds.length > 0 },
  )

  const memoizedData = useMemo(() => {
    return queryInfo.data?.map((variantId) => variantId)
  }, [queryInfo.data])

  return { ...queryInfo, data: memoizedData }
}
